/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */

/**
 * הגדרות הממשק שהריסיילר קיבל - עיצוב, מה מותר, ואילו קבצים להציג.
 *
 * הכל מגיע מ-`getResellerData`, שמקבל אותו מהבילינג דרך ה-SSO. המסך אינו מחזיק כאן
 * שום החלטה משלו, וזו הנקודה: רשימת ההגדרות, ברירות המחדל שלהן והצבעים יושבים
 * בקטלוג שבבילינג (`ycserver/src/class/PortalSettings.ts`), וכל דבר שנכתב גם כאן היה
 * מסכים איתו עד שאחד מהם ישתנה.
 *
 * שני כללים שכל הקובץ הזה בנוי עליהם:
 *
 *   **`undefined` אינו `false`.** הגדרה שלא הגיעה פירושה "אין מידע" - ריסיילר שאין לו
 *   תת-דומיין, או תמונה שטרם נטענה - וההתנהגות הנכונה אז היא זו שהייתה למסך קודם.
 *   ברירת מחדל סוגרת כאן הייתה חוסמת הרשמה לכל מי שטרם עבר למנגנון.
 *
 *   **העיצוב הוא משתני CSS, ולא מחרוזות שהקוד מכיר.** `cssVars` מגיע מהשרת כמפה של
 *   שם משתנה לערך, והמסך מגדיר אותה כמות שהיא. אסימון צבע שיתווסף בקטלוג מגיע ל-CSS
 *   בלי שורת קוד כאן.
 *
 */

/**
 * ערכת העיצוב שבתוקף, או `null` כשאין מידע.
 *
 * **`null` הוא תשובה, ולא ברירת מחדל.** `theme: null` מהשרת פירושו "אין מידע" -
 * ריסיילר שאין לו תת-דומיין בבילינג, או בילינג שטרם עודכן - ואז ההתנהגות הנכונה היא
 * המסך שהיה, ולא ערכה שנבחרה עבורו. ברירת המחדל של הערכות היא החלטה של הבילינג
 * (PortalSettings.DEFAULT_PRESET), והיא מגיעה בתשובה במפורש; לנחש אותה כאן פירושו
 * לשנות את המסך של מי שהמערכת אינה יודעת עליו דבר.
 *
 * זו הסיבה ש-applyTheme אינו מסמן data-portal-preset כשאין ערכה: ה-CSS נופל
 * ל-:root החשוף, שהוא בדיוק העיצוב שהיה לפני המנגנון.
 */
export const presetOf = (resellerData) => resellerData?.theme?.preset || null;

/**
 * הערכות שמסך ההתחברות שלהן הוא מסך הסלולר.
 *
 * **רשימה ולא ערך אחד, כי בבילינג יש ערכה לכל מיתוג ולא ערכה לכל מסך.** "סים קהילות"
 * ו"סים מהדרין" הן שתי ערכות שהמסך שלהן זהה, וההבדל ביניהן הוא הפלטה - שממילא מגיעה
 * בתשובה. `cellular` הוא המפתח הישן: הבילינג מתרגם אותו לפני שהוא נשלח, והוא נשאר
 * כאן כדי שפריסה מתגלגלת לא תפיל את המסך של מי שכבר עומד עליו.
 */
const CELLULAR_LOGIN_PRESETS = ['sim-kehilot', 'simehadrin', 'cellular'];

/**
 * הערכה של הדגל הישן.
 *
 * `redirect_to_sim` אומר "המסך שלו הוא מסך הסלולר" ואינו אומר איזה מיתוג, ולכן הוא
 * נקרא כמיתוג של סים קהילות - זה בדיוק המסך שריסיילר כזה מקבל היום. אותה הכרעה בדיוק
 * כמו ב-`legacyPayload` שב-SSO, כי זה אותו דגל.
 */
const LEGACY_CELLULAR_PRESET = 'sim-kehilot';

/**
 * הערכה שהמסך מצויר בה - הערכה, ואם אין, מה שהדגל הישן אומר.
 *
 * `redirect_to_sim` הוא המילון הידני ב-`config.json` של ה-SSO, והוא הנפילה לאחור
 * לריסיילר שהבילינג טרם שלח עליו ערכה.
 *
 * **למה זה חייב להיות במקום אחד.** הדגל הכריע איזה מסך נפתח (`RedirectByReseller`),
 * אבל לא נכתב על `<html>` - כלומר מסך ההתחברות של ריסיילר כזה נצבע בצהוב מהגיבויים
 * שב-`stylesSim.css`, ואילו ההמשך של ההתחברות, שהוא הזרימה המשותפת, היה מצויר
 * בערכה של ימות המשיח. אותה התחברות בשני עיצובים, לפי המסך.
 *
 * `null` נשמר כתשובה: ריסיילר שאין עליו מידע ואין לו דגל אינו מקבל ערכה שנבחרה
 * עבורו - ראה `presetOf`.
 */
export const effectivePreset = (resellerData) =>
    presetOf(resellerData) || (resellerData?.redirect_to_sim === true ? LEGACY_CELLULAR_PRESET : null);

/**
 * האם ההתחברות של הריסיילר היא מסך הסלולר.
 *
 * זו השאלה שהניתוב שואל, והיא אינה "מה הערכה": שלוש ערכות מובילות לאותו מסך, ומסך
 * שמשווה למפתח אחד היה מפספס את השתיים האחרות בשקט - כלומר ריסיילר שבחר את המיתוג
 * שלו היה מקבל את הזרימה המלאה.
 */
export const isCellularLogin = (resellerData) =>
    CELLULAR_LOGIN_PRESETS.includes(effectivePreset(resellerData));

/**
 * האם הגדרה תפקודית פעילה.
 *
 * **המסך אינו מסתמך על זה לביטחון.** כל הגדרה שיש לה משמעות נאכפת בשרת (ראה
 * `featureSpec.enforcement` בקטלוג), וכאן היא רק קובעת אם להראות את הכפתור. מסך
 * שמסתיר פעולה שהשרת מקבל, או מציג פעולה שהשרת דוחה, הוא מסך שאינו מסכים עם המערכת.
 */
export const isFeatureOn = (resellerData, key, fallback = true) => {
    const value = resellerData?.features?.[key];
    return typeof value === 'boolean' ? value : fallback;
};

/**
 * הכתובת של נכס מיתוג, או `null` כשאין.
 *
 * שתי שכבות נפילה לאחור, בסדר הזה:
 *
 *   1. `assets[key]` - נכס שהריסיילר העלה. כתובת יחסית עם חותמת תוכן.
 *   2. `public_path` - תיקיית הנכסים הידנית ב-`config.json` של ה-SSO. זו הדרך היחידה
 *      שהייתה עד היום, והיא נשארת: ריסיילר שלא העלה דבר ממשיך לקבל את מה שיש לו.
 *
 *   `null` = גם זה אין, והקורא מציג את ברירת המחדל שהיישום מגיע איתה.
 *
 * `legacyName` נדרש כי שמות הקבצים בתיקייה הידנית אינם מפתחות הקטלוג
 * (`favicon.ico`, `logo_new.png`), ולא כדאי לשנות אותם - הם קיימים על הדיסק בפריסות.
 */
export const assetUrl = (resellerData, key, legacyName) => {
    const fromBilling = resellerData?.assets?.[key];
    if (fromBilling) return fromBilling;
    if (resellerData?.public_path && legacyName) return `${resellerData.public_path}/${legacyName}`;
    return null;
};

/**
 * התמונה הגדולה של מסך ההתחברות הסלולרי.
 *
 * שלוש שכבות, בסדר הזה - וזו אותה שרשרת בדיוק כמו `heroBackground` במסך הבית של
 * האזור האישי (`kehilot-clients`), כי זה אותו נכס בקטלוג (`hero`, שמוצהר על שני
 * הממשקים):
 *
 *   1. `assets.hero` - תמונה שהריסיילר העלה בבילינג.
 *   2. תמונת הערכה - זו שנבנתה למיתוג, ומגיעה עם הקוד.
 *   3. `null` - אין תמונה, והמסך אינו מצייר תג ריק.
 *
 * **למה מפה ולא תמונה אחת.** התמונה של סים קהילות (`device.jpg`) הייתה כתובה קשיח
 * במסך, ולכן ריסיילר סלולרי אחר קיבל את **התמונה של סים קהילות** - וזה מה שקרה לסים
 * מהדרין. המפה כאן היא לפי ערכה, כלומר לפי מיתוג, ומיתוג שאין לו תמונה בקוד מקבל
 * `null` ולא את זו של אחר. מי שאין לו תמונה בקוד ורוצה אחת מעלה אותה בבילינג.
 */
const PRESET_HERO = {
    'sim-kehilot': '/images/sim/device.jpg',
    'simehadrin': '/images/sim/simehadrin.png',
    // המפתח הישן. אותה תמונה, כי הוא אותו מיתוג.
    'cellular': '/images/sim/device.jpg',
};

export const heroUrl = (resellerData) =>
    assetUrl(resellerData, 'hero', null) || PRESET_HERO[effectivePreset(resellerData)] || null;

/**
 * הקישוט בתחתית מסך הסלולר - הציור שמגיע עם המיתוג.
 *
 * מפה ולא תמונה אחת, מאותו נימוק בדיוק כמו ב-`PRESET_HERO`: `people.png` הוא ציור של
 * סים קהילות, וכשהוא היה כתוב קשיח הוא צויר גם על המסך של סים מהדרין. מיתוג שאין לו
 * ציור אינו מקבל את זה של אחר - הוא מקבל מסך בלי קישוט, שהוא מסך נקי ולא מסך שבור.
 *
 * אין כאן שכבת נכס שהועלה, ובמכוון: זה קישוט של הגיליון ולא תמונה שהמסך נשען עליה,
 * ולקטלוג אין מפתח נכס עבורו. מי שרוצה תמונה משלו מעלה `hero`.
 */
const PRESET_DECOR = {
    'sim-kehilot': '/images/sim/people.png',
    'cellular': '/images/sim/people.png',
};

export const decorUrl = (resellerData) => PRESET_DECOR[effectivePreset(resellerData)] || null;

/**
 * החלת העיצוב על העמוד.
 *
 * שני דברים, ושניהם על `<html>` ולא על `<body>`: משתני ה-CSS צריכים להיות זמינים לכל
 * העמוד כולל למה שמצויר מחוץ ל-body של האפליקציה (דיאלוגים של MUI נכנסים ל-portal),
 * ו-`data-portal-preset` הוא מה שמאפשר ל-CSS לומר "בערכה הזו אחרת" בלי שהקוד יבחר
 * גיליון סגנון.
 *
 * המשתנים מוגדרים ואינם מוסרים כשאין: `var(--portal-primary, #20678B)` בגיליון נופל
 * לברירת המחדל מעצמו, ולכן ריסיילר בלי עיצוב אינו צריך שנמחק דבר.
 */
export const applyTheme = (resellerData) => {
    const root = document.documentElement;
    if (!root) return;

    // ערכה שאין לה תשובה אינה מסומנת, וגם מוסרת אם סומנה קודם: המאפיין הוא מה שבוחר
    // בלוק צבעים ב-CSS, ולהשאיר אותו על ריסיילר שאין עליו מידע זה לבחור בשבילו.
    const preset = effectivePreset(resellerData);
    if (preset) root.setAttribute('data-portal-preset', preset);
    else root.removeAttribute('data-portal-preset');

    /**
     * השלד - איזו פריסה מצוירת, בנפרד מאיזו פלטה.
     *
     * **שני מאפיינים ולא אחד, כי הם שתי שאלות.** הפלטה היא של המיתוג ולכן היא תלויה
     * בערכה עצמה; הפריסה משותפת לכל המיתוגים הסלולריים, ו-CSS שמונה אותם בכל כלל היה
     * שלושה סלקטורים בכל שורה - ורביעי שיישכח במיתוג הבא. כאן הרשימה נמצאת במקום אחד
     * (`CELLULAR_LOGIN_PRESETS`), והוא גם זה שהניתוב קורא.
     */
    if (isCellularLogin(resellerData)) root.setAttribute('data-portal-skeleton', 'cellular');
    else root.removeAttribute('data-portal-skeleton');

    const cssVars = resellerData?.theme?.cssVars;
    if (!cssVars) return;
    for (const [name, value] of Object.entries(cssVars)) {
        // רק שמות בצורה שהשרת מייצר. הערך עצמו מאומת בשרת (hex בלבד), והשם נבדק כאן:
        // `setProperty` על שם שרירותי הוא הזרקה לגיליון הסגנון של העמוד.
        if (/^--[a-z0-9-]+$/.test(name)) root.style.setProperty(name, String(value));
    }
};

/**
 * החלפת סמל הלשונית, האייקונים והמניפסט.
 *
 * מחליף רק את מה שיש לו כתובת: תג שנשאר על ברירת המחדל של היישום עדיף על תג שמצביע
 * לקובץ שאינו קיים, כי השני הוא בקשה שנכשלת בכל טעינה.
 */
export const applyDocumentAssets = (resellerData) => {
    const items = [
        { selector: "link[rel*='icon']", attribute: 'href', key: 'favicon', legacy: 'favicon.ico' },
        { selector: "link[rel*='apple-touch-icon']", attribute: 'href', key: 'icon192', legacy: 'logo192.png' },
        // המניפסט אינו נכס בקטלוג: הוא JSON ולא תמונה, והוא מפנה בעצמו לאייקונים.
        // הוא נשאר בתיקייה הידנית עד שיהיה מי שמייצר אותו.
        { selector: "link[rel*='manifest']", attribute: 'href', key: null, legacy: 'manifest.json' },
    ];

    for (const item of items) {
        const element = document.querySelector(item.selector);
        if (!element) continue;
        const url = item.key
            ? assetUrl(resellerData, item.key, item.legacy)
            : (resellerData?.public_path ? `${resellerData.public_path}/${item.legacy}` : null);
        if (url) element[item.attribute] = url;
    }
};

/** שם המיתוג לכותרות. הקצר קודם, אחריו המלא, ו-null כשאין. */
export const brandName = (resellerData) => resellerData?.shortName || resellerData?.name || null;

/** משפט התיאור של הריסיילר, או null. ברירת המחדל שלו בבילינג היא `public_description`. */
export const brandDescription = (resellerData) => resellerData?.brandDescription || null;

/**
 * המפתחות של שם המוצר ושל משפט התיאור שלו, בקובצי השפה.
 *
 * הנפילה לאחור היא **מתורגמת** ולא מחרוזת בקוד: ריסיילר שאין לנו את שמו או את התיאור
 * שלו מקבל את אלה של המוצר, והם נכתבים אחרת בעברית ובאנגלית.
 */
const PRODUCT_NAME_KEY = 'Yemot Hamashiach';
const PRODUCT_TAGLINE_KEY = 'the advanced communication solutions company';

/**
 * טקסט שנושא את שם המיתוג של הריסיילר.
 *
 * למה עוטף ולא `t(key, { brand })` בכל מקום: שני דברים חוזרים בכל אחד מארבעת
 * המקומות, וכל אחד מהם נשכח בקלות בחמישי.
 *
 *   **הנפילה לאחור.** ריסיילר שאין לנו את שמו מקבל את שם המוצר, מתורגם.
 *
 *   **`escapeValue: false`.** i18next מסמן ערכים שמוזרקים לתרגום כברירת מחדל, ושמות
 *   ריסיילרים מכילים גרשיים - `סים קהילות בע"מ` היה מוצג כ-`סים קהילות בע&quot;מ`.
 *   הטקסט הזה נמסר ל-React כ-text node, ש-React ממילא מסמן, ולכן הסימון של i18next
 *   כאן הוא סימון כפול ולא הגנה.
 */
export const tBrand = (t, key, resellerData) => t(key, {
    brand: brandName(resellerData) || t(PRODUCT_NAME_KEY),
    description: brandDescription(resellerData) || t(PRODUCT_TAGLINE_KEY),
    interpolation: { escapeValue: false },
});
